import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Briefcase, Building2, Loader2, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { customerSearchView, isSearchableCustomerQuery } from "@/lib/customer-search-state";
import { useQuickBook } from "@/lib/quick-book-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { TierBadge } from "@/components/ui/tier-badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

type Tier = "T1" | "T2" | "T3";

interface CustomerOption {
    id: string;
    company_name: string;
}

interface SiteOption {
    id: string;
    name: string;
    address_line: string;
    city: string;
    water_provider_id?: string | null;
}

interface JobType {
    id: string;
    name: string;
    code?: string | null;
    trade_name: string;
    default_duration_minutes: number;
    is_active: boolean;
}

/** Mirror of jobs/form.tsx COMPLIANCE_JOB_TYPE_PREFIXES — compliance jobs
 *  (backflow / fire-safety) need a due-by deadline and a jurisdiction so the
 *  result can later be filed with the authority. */
const COMPLIANCE_JOB_TYPE_PREFIXES = ["backflow_", "fire_ext_", "fire_safety_"];
function isComplianceJobTypeCode(code: string | undefined | null): boolean {
    if (!code) return false;
    const c = code.toLowerCase();
    return COMPLIANCE_JOB_TYPE_PREFIXES.some((p) => c.startsWith(p));
}

const TIER_OPTIONS: { value: Tier; label: string }[] = [
    { value: "T1", label: "T1 · Priority" },
    { value: "T2", label: "T2 · Standard" },
    { value: "T3", label: "T3 · Overflow" },
];

function useDebounce(value: string, delay: number): string {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}

/**
 * Quick Book: global right slide-in for booking a new job without leaving the
 * current screen. Mirrors the customer typeahead from the New Job form and the
 * job-create POST from the call dock's intake form. The panel is an overlay —
 * it never calls navigate() on open/close, so the user lands back on whatever
 * screen they were on. The only navigation is the explicit "View job" action
 * on the success toast.
 */
export function QuickBookPanel() {
    const { isOpen, close } = useQuickBook();
    return (
        <Sheet
            open={isOpen}
            onOpenChange={(o) => {
                if (!o) close();
            }}
        >
            <SheetContent className="w-[28rem] max-w-full gap-0 p-0">
                <SheetHeader className="bg-secondary">
                    <SheetTitle className="flex items-center gap-2">
                        <Briefcase className="h-5 w-5 text-primary" />
                        Quick Book — New Job
                    </SheetTitle>
                </SheetHeader>
                {/* Keyed remount-on-open keeps the form state fresh each time. */}
                {isOpen && <QuickBookForm onDone={close} />}
            </SheetContent>
        </Sheet>
    );
}

export interface QuickBookFormProps {
    onDone: () => void;
    /** Pre-select a customer (e.g. embedded in the Call Panel after intake). */
    initialCustomerId?: string;
    initialCustomerName?: string;
    /**
     * Called with the new job id after a successful create. When provided, the
     * form does NOT auto-close via onDone — the host (Call Panel) drives the
     * next view. When omitted (standalone QuickBookPanel), onDone closes the
     * sheet as before.
     */
    onCreated?: (jobId: string) => void;
}

export function QuickBookForm({ onDone, initialCustomerId, initialCustomerName, onCreated }: QuickBookFormProps) {
    const navigate = useNavigate();
    const qc = useQueryClient();

    const [title, setTitle] = useState("");
    const [customerId, setCustomerId] = useState(initialCustomerId ?? "");
    const [customerName, setCustomerName] = useState(initialCustomerName ?? "");
    const [siteId, setSiteId] = useState("");
    const [jobTypeId, setJobTypeId] = useState("");
    const [dueBy, setDueBy] = useState("");
    const [tier, setTier] = useState<Tier>("T2");

    // Customer typeahead
    const [customerSearch, setCustomerSearch] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const customerRef = useRef<HTMLDivElement>(null);
    const debouncedSearch = useDebounce(customerSearch, 300);

    // Tracks whether the office manually picked a tier so we don't clobber it
    // when the selected customer's stored tier loads in.
    const tierTouchedRef = useRef(false);

    const { data: customerResults, isFetching: customerSearchFetching } = useQuery({
        queryKey: ["quick-book", "customer-search", debouncedSearch],
        queryFn: () =>
            api.get<{ customers: CustomerOption[] }>(
                `/api/customers?q=${encodeURIComponent(debouncedSearch)}&limit=20`,
            ),
        enabled: !customerId && isSearchableCustomerQuery(debouncedSearch),
    });

    // Pull the chosen customer's stored tier so the badge reflects reality and
    // we only PATCH when the office actually changes it.
    const { data: customerDetail } = useQuery({
        queryKey: ["quick-book", "customer", customerId],
        queryFn: () =>
            api.get<{ customer: { id: string; company_name: string; priority_tier: Tier } }>(
                `/api/customers/${customerId}`,
            ),
        enabled: !!customerId,
    });

    const storedTier = customerDetail?.customer.priority_tier;
    useEffect(() => {
        if (storedTier && !tierTouchedRef.current) setTier(storedTier);
    }, [storedTier]);

    const { data: sitesData } = useQuery({
        queryKey: ["quick-book", "sites", customerId],
        queryFn: () => api.get<{ sites: SiteOption[] }>(`/api/customers/${customerId}/sites`),
        enabled: !!customerId,
    });

    const { data: jobTypesData } = useQuery({
        queryKey: ["job-types", "active"],
        queryFn: () => api.get<{ jobTypes: JobType[] }>("/api/job-types?activeOnly=true"),
        staleTime: 5 * 60_000,
    });

    const customers = customerResults?.customers ?? [];
    // #822: dropdown state comes from this verdict, never customers.length
    // alone — no stale rows, no "No customers found" mid-search.
    const customerView = customerSearchView({
        inputText: customerSearch,
        debouncedText: debouncedSearch,
        isFetching: customerSearchFetching,
        resultCount: customers.length,
    });
    const sites = sitesData?.sites ?? [];
    const jobTypes = jobTypesData?.jobTypes ?? [];

    // Group active job types by trade for the optgroup picker (mirrors New Job).
    const jobTypesByTrade = useMemo(() => {
        const groups = new Map<string, JobType[]>();
        for (const jt of jobTypes) {
            const arr = groups.get(jt.trade_name) ?? [];
            arr.push(jt);
            groups.set(jt.trade_name, arr);
        }
        return Array.from(groups.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([trade, types]) => ({
                trade,
                types: types.slice().sort((a, b) => a.name.localeCompare(b.name)),
            }));
    }, [jobTypes]);

    // Auto-pick the only location so the common single-site customer is one
    // tap to book.
    useEffect(() => {
        if (sites.length === 1 && !siteId) setSiteId(sites[0].id);
    }, [sites, siteId]);

    // Close dropdown on click outside.
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (customerRef.current && !customerRef.current.contains(e.target as Node)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const selectCustomer = (c: CustomerOption) => {
        setCustomerId(c.id);
        setCustomerName(c.company_name);
        setCustomerSearch("");
        setDropdownOpen(false);
        setSiteId("");
    };

    const clearCustomer = () => {
        setCustomerId("");
        setCustomerName("");
        setSiteId("");
        setCustomerSearch("");
        tierTouchedRef.current = false;
        setTier("T2");
    };

    // C1: a brand-new customer needs a real address (a site) or the job can
    // never be booked. Rather than create a name-only shell here (which left
    // the Site dropdown empty and Book Job permanently disabled), send the
    // office to the full intake form, which requires an address. Pre-fills the
    // company name from whatever they typed.
    const goToNewCustomer = () => {
        const name = customerSearch.trim();
        navigate(`/customers/new${name ? `?company=${encodeURIComponent(name)}` : ""}`);
        onDone();
    };

    const createJobMutation = useMutation({
        mutationFn: async () => {
            // Apply a changed tier to an existing customer before booking so the
            // dispatch backlog sees the right priority. New customers already
            // carry the tier from create above.
            if (storedTier && storedTier !== tier) {
                await api.patch(`/api/customers/${customerId}`, { priorityTier: tier });
            }
            return api.post<{ id: string }>("/api/jobs", {
                title: title.trim(),
                customerId,
                siteId,
                jobTypeId: jobTypeId || null,
                status: "awaiting_scheduling",
                dueBy: dueBy || null,
            });
        },
        onSuccess: (resp) => {
            qc.invalidateQueries({ queryKey: ["jobs"] });
            // H7: surface the new job in the dispatch backlog immediately rather
            // than after a manual refresh.
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            qc.invalidateQueries({ queryKey: ["work-pulse"] });
            toast.success("Job created — added to Awaiting Dispatch", {
                description: title.trim(),
                action: {
                    label: "View job",
                    onClick: () => navigate(`/jobs/${resp.id}`),
                },
            });
            // When embedded (Call Panel), hand the new job id to the host so it
            // can show its own confirmation step instead of closing.
            if (onCreated) onCreated(resp.id);
            else onDone();
        },
        onError: () => toast.error("Failed to create the job. Please try again."),
    });

    const selectedJobType = jobTypes.find((jt) => jt.id === jobTypeId) ?? null;
    const isComplianceJob = isComplianceJobTypeCode(selectedJobType?.code);
    const selectedSite = sites.find((s) => s.id === siteId) ?? null;
    // H6: a compliance job at a site with no jurisdiction can be created but
    // never filed. Warn (don't block) so the office can still book it.
    const missingJurisdiction = isComplianceJob && !!selectedSite && !selectedSite.water_provider_id;

    const canSubmit = !!title.trim() && !!customerId && !!siteId && !!jobTypeId && !(isComplianceJob && !dueBy);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim() || !customerId || !siteId) {
            toast.error("Add a title, customer, and location before booking.");
            return;
        }
        // H5: job type is required so pricing, duration, and compliance rules
        // apply — mirrors the full New Job form.
        if (!jobTypeId) {
            toast.error("Pick a job type so pricing, duration, and compliance rules apply.");
            return;
        }
        if (isComplianceJob && !dueBy) {
            toast.error(
                "Pick a Due-by date — backflow & fire-safety jobs need a deadline so the auto-scheduler doesn't slip past it.",
            );
            return;
        }
        createJobMutation.mutate();
    };

    return (
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col" autoComplete="off">
            <div className="flex-1 space-y-4 overflow-y-auto p-4">
                {/* Customer */}
                <div className="space-y-1.5">
                    <label className="text-sm font-medium">
                        Customer <span className="text-destructive">*</span>
                    </label>
                    {customerId ? (
                        <div className="flex items-center gap-2 rounded-lg border bg-muted/30 p-2.5">
                            <Building2 className="h-4 w-4 text-foreground/80" />
                            <span className="flex-1 truncate font-medium">{customerName || customerId}</span>
                            <TierBadge tier={storedTier ?? tier} />
                            <button
                                type="button"
                                onClick={clearCustomer}
                                className="rounded p-1 hover:bg-muted"
                                aria-label="Clear customer"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    ) : (
                        <div className="relative" ref={customerRef}>
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/80" />
                                    <Input
                                        placeholder="Search by name, address, or contact…"
                                        value={customerSearch}
                                        onChange={(e) => {
                                            setCustomerSearch(e.target.value);
                                            setDropdownOpen(true);
                                        }}
                                        onFocus={() => setDropdownOpen(true)}
                                        className="pl-9"
                                    />
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    onClick={goToNewCustomer}
                                    title="Create a new customer"
                                >
                                    <Plus className="h-4 w-4" />
                                </Button>
                            </div>
                            {dropdownOpen &&
                                (customerView === "results" || customerView === "recents") &&
                                customers.length > 0 && (
                                    <div className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border bg-card shadow-lg">
                                        {customerView === "recents" && (
                                            <div className="border-b bg-muted/30 px-3 py-1.5 text-[11px] uppercase tracking-wide text-foreground/80">
                                                Recent customers
                                            </div>
                                        )}
                                        {customers.map((c) => (
                                            <button
                                                key={c.id}
                                                type="button"
                                                className="w-full border-b px-3 py-2.5 text-left text-sm transition-colors last:border-b-0 hover:bg-muted/50"
                                                onClick={() => selectCustomer(c)}
                                            >
                                                {c.company_name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            {dropdownOpen && customerView === "keep-typing" && (
                                <div className="absolute z-20 mt-1 w-full rounded-lg border bg-card p-3 text-sm text-foreground/80 shadow-lg">
                                    Keep typing — at least 2 letters to search.
                                </div>
                            )}
                            {dropdownOpen && customerView === "searching" && (
                                <div
                                    className="absolute z-20 mt-1 w-full rounded-lg border bg-card p-3 text-sm text-foreground/80 shadow-lg"
                                    aria-live="polite"
                                >
                                    Searching…
                                </div>
                            )}
                            {dropdownOpen && customerView === "empty" && (
                                <div className="absolute z-20 mt-1 w-full rounded-lg border bg-card p-3 text-sm text-foreground/80 shadow-lg">
                                    No customers found.{" "}
                                    <button
                                        type="button"
                                        className="text-primary hover:underline"
                                        onClick={goToNewCustomer}
                                    >
                                        Create new customer
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Location */}
                <div className="space-y-1.5">
                    <label htmlFor="qb-site" className="text-sm font-medium">
                        Site / Location <span className="text-destructive">*</span>
                    </label>
                    {/* H4: a customer with zero locations can't be booked. Point
                        the office to add one instead of leaving a dead dropdown. */}
                    {customerId && sites.length === 0 ? (
                        <div className="rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-sm dark:border-amber-500/40 dark:bg-amber-500/10">
                            <p className="text-amber-800 dark:text-amber-200">
                                This customer has no locations yet. Add a location before booking.
                            </p>
                            {/* G5: same-tab — close the sheet and go; cmd/ctrl+click
                                still opens a new tab (and keeps the sheet). */}
                            <Link
                                to={`/customers/${customerId}`}
                                onClick={(e) => {
                                    if (!e.metaKey && !e.ctrlKey && !e.shiftKey) onDone();
                                }}
                                className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-amber-400 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 dark:bg-transparent dark:text-amber-200"
                            >
                                <Plus className="h-3.5 w-3.5" /> Add a location
                            </Link>
                        </div>
                    ) : (
                        <Select
                            id="qb-site"
                            value={siteId}
                            onChange={(e) => setSiteId(e.target.value)}
                            disabled={!customerId}
                        >
                            <option value="">{customerId ? "Select location…" : "Choose a customer first"}</option>
                            {sites.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.name}
                                    {s.city ? ` — ${s.city}` : ""}
                                </option>
                            ))}
                        </Select>
                    )}
                </div>

                {/* Title */}
                <div className="space-y-1.5">
                    <label htmlFor="qb-title" className="text-sm font-medium">
                        Job Title <span className="text-destructive">*</span>
                    </label>
                    <Input
                        id="qb-title"
                        placeholder="e.g. Annual Backflow Test"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />
                </div>

                {/* Job type */}
                <div className="space-y-1.5">
                    <label htmlFor="qb-job-type" className="text-sm font-medium">
                        Job Type <span className="text-destructive">*</span>
                    </label>
                    <Select id="qb-job-type" value={jobTypeId} onChange={(e) => setJobTypeId(e.target.value)}>
                        <option value="">Select job type…</option>
                        {jobTypesByTrade.map((group) => (
                            <optgroup key={group.trade} label={group.trade}>
                                {group.types.map((jt) => (
                                    <option key={jt.id} value={jt.id}>
                                        {jt.name}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                    </Select>
                    {/* H6: jurisdiction warning — compliance jobs at a site with no
                        water provider can be booked but never filed. */}
                    {missingJurisdiction && (
                        <p className="rounded-md border border-amber-300/60 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
                            No jurisdiction (water provider) is set on this location. You can still book the job, but
                            the result can't be filed with the authority until the location's water provider is set.{" "}
                            <Link
                                to={`/sites/${siteId}`}
                                onClick={(e) => {
                                    if (!e.metaKey && !e.ctrlKey && !e.shiftKey) onDone();
                                }}
                                className="font-medium underline hover:no-underline"
                            >
                                Set it now →
                            </Link>
                        </p>
                    )}
                </div>

                {/* Due date */}
                <div className="space-y-1.5">
                    <label htmlFor="qb-due" className="text-sm font-medium">
                        Due Date{isComplianceJob && <span className="text-destructive"> *</span>}
                    </label>
                    <Input id="qb-due" type="date" value={dueBy} onChange={(e) => setDueBy(e.target.value)} />
                    <p className="text-xs text-foreground/80">
                        {isComplianceJob
                            ? "Required for backflow & fire-safety work — the deadline the auto-scheduler won't slip past."
                            : "Deadline the auto-scheduler won't slip past. Optional for non-compliance work."}
                    </p>
                </div>

                {/* Priority tier */}
                <div className="space-y-1.5">
                    <label htmlFor="qb-tier" className="flex items-center gap-2 text-sm font-medium">
                        Customer Priority Tier
                        <TierBadge tier={tier} />
                    </label>
                    <Select
                        id="qb-tier"
                        value={tier}
                        onChange={(e) => {
                            tierTouchedRef.current = true;
                            setTier(e.target.value as Tier);
                        }}
                    >
                        {TIER_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </Select>
                    {customerId && storedTier && storedTier !== tier && (
                        <p className="text-xs text-primary">
                            Updates this customer's tier from {storedTier} to {tier} when you book.
                        </p>
                    )}
                </div>
            </div>

            {/* Footer actions */}
            <div className="flex items-center gap-2 border-t p-4">
                <Button type="submit" disabled={!canSubmit || createJobMutation.isPending} className="flex-1">
                    {createJobMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Briefcase className="h-4 w-4" />
                    )}
                    Book Job
                </Button>
                <Button type="button" variant="outline" onClick={onDone}>
                    Cancel
                </Button>
            </div>
        </form>
    );
}
